import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { MessageSquareText, Lock, Flag, CornerDownRight } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useFlags } from "@/lib/flags";
import { promptLogin } from "@/lib/authRedirect";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Label } from "@/components/ui/label";

const MAX_LEN = 2000;
const REASONS = [
  { v: "spam", label: "Spam atau promosi" },
  { v: "kasar", label: "Bahasa kasar atau menyerang" },
  { v: "informasi_salah", label: "Informasi menyesatkan" },
  { v: "lainnya", label: "Lainnya" },
];

const errMsg = (e, fallback) => e?.response?.data?.detail || fallback;

function formatWhen(iso) {
  if (!iso) return "";
  return new Date(iso).toLocaleDateString("id-ID", { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
}

function Avatar({ name, picture }) {
  if (picture) return <img src={picture} alt="" className="w-9 h-9 rounded-full object-cover flex-shrink-0" />;
  return (
    <div className="w-9 h-9 rounded-full bg-[#0F1F3D]/10 text-[#0F1F3D] text-xs font-bold flex items-center justify-center flex-shrink-0">
      {(name || "P").split(" ").map((w) => w[0]).slice(0, 2).join("").toUpperCase()}
    </div>
  );
}

function Composer({ slug, parentId, onPosted, onCancel, compact }) {
  const [body, setBody] = useState("");
  const [busy, setBusy] = useState(false);
  const tid = parentId ? `reply-${parentId}` : "comment";
  const submit = async () => {
    if (body.trim().length < 2) return toast.error("Tanggapan terlalu pendek.");
    setBusy(true);
    try {
      const r = await api.post(`/blog/${slug}/discussion`, { body, parent_id: parentId || null });
      toast.success(r.data.message || "Tanggapan Anda berhasil dipublikasikan.");
      setBody("");
      onPosted(r.data.comment);
    } catch (e) {
      toast.error(errMsg(e, "Gagal mengirim tanggapan. Coba lagi."));
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className={compact ? "mt-3" : ""} data-testid={`${tid}-composer`}>
      <Textarea
        value={body}
        maxLength={MAX_LEN}
        onChange={(e) => setBody(e.target.value)}
        placeholder={parentId ? "Tulis balasan Anda…" : "Bagikan pandangan Anda tentang artikel ini…"}
        className="bg-white border-[#E5E1D8] rounded-xl min-h-[96px] text-[15px] focus-visible:ring-[#E07A5F]/40"
        data-testid={`${tid}-input`}
      />
      <div className="mt-2 flex items-center justify-between gap-3">
        <span className="text-xs text-[#8A8F99] tabular-nums" data-testid={`${tid}-counter`}>{body.length}/{MAX_LEN}</span>
        <div className="flex gap-2">
          {onCancel && (
            <Button variant="ghost" size="sm" onClick={onCancel} className="rounded-full" data-testid={`${tid}-cancel-btn`}>Batal</Button>
          )}
          <Button
            size="sm" onClick={submit} disabled={busy || body.trim().length < 2}
            className="rounded-full bg-[#0F1F3D] hover:bg-[#1E3A8A] text-white px-5"
            data-testid={`${tid}-submit-btn`}
          >
            {busy ? "Mengirim…" : parentId ? "Kirim balasan" : "Kirim tanggapan"}
          </Button>
        </div>
      </div>
    </div>
  );
}

function ReportDialog({ comment, onClose }) {
  const [reason, setReason] = useState("spam");
  const [detail, setDetail] = useState("");
  const [busy, setBusy] = useState(false);
  const send = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/article-comments/${comment.comment_id}/report`, { reason, detail });
      toast.success(r.data.message);
      onClose();
    } catch (e) {
      toast.error(errMsg(e, "Gagal mengirim laporan."));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog open={!!comment} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="discussion-report-dialog">
        <DialogHeader>
          <DialogTitle>Laporkan tanggapan</DialogTitle>
          <DialogDescription>Laporan Anda akan ditinjau tim kami. Tanggapan tidak otomatis disembunyikan.</DialogDescription>
        </DialogHeader>
        <RadioGroup value={reason} onValueChange={setReason} className="space-y-2">
          {REASONS.map((r) => (
            <div key={r.v} className="flex items-center gap-2">
              <RadioGroupItem value={r.v} id={`rr-${r.v}`} data-testid={`report-reason-${r.v}`} />
              <Label htmlFor={`rr-${r.v}`}>{r.label}</Label>
            </div>
          ))}
        </RadioGroup>
        <Textarea value={detail} maxLength={500} onChange={(e) => setDetail(e.target.value)}
          placeholder="Keterangan tambahan (opsional)" data-testid="report-detail-input" />
        <DialogFooter>
          <Button variant="ghost" onClick={onClose} data-testid="report-cancel-btn">Batal</Button>
          <Button onClick={send} disabled={busy} className="bg-[#BE5730] hover:bg-[#A44A33] text-white" data-testid="report-submit-btn">
            {busy ? "Mengirim…" : "Kirim laporan"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function CommentBody({ c, onReport, user }) {
  return (
    <div className="flex gap-3" data-testid={`discussion-comment-${c.comment_id}`}>
      <Avatar name={c.user_name} picture={c.user_picture} />
      <div className="min-w-0 flex-1">
        <div className="flex items-baseline gap-2 flex-wrap">
          <span className="text-sm font-semibold text-[#1F2937]">{c.user_name}</span>
          <time className="text-xs text-[#8A8F99]">{formatWhen(c.created_at)}</time>
        </div>
        <p className="mt-1 text-[15px] leading-relaxed text-[#374151] whitespace-pre-line break-words" data-testid={`discussion-comment-body-${c.comment_id}`}>{c.body}</p>
        {user && (
          <button onClick={() => onReport(c)} data-testid={`discussion-report-btn-${c.comment_id}`}
            className="mt-1 inline-flex items-center gap-1 text-xs text-[#8A8F99] hover:text-[#BE5730] transition-colors">
            <Flag size={12} /> Laporkan
          </button>
        )}
      </div>
    </div>
  );
}

function Thread({ c, slug, open, user, onReport, onReply, onLogin }) {
  const [replying, setReplying] = useState(false);
  return (
    <li className="py-6 border-b border-[#EFEBE3] last:border-0" data-testid={`discussion-thread-${c.comment_id}`}>
      <CommentBody c={c} onReport={onReport} user={user} />
      <div className="ml-12">
        {open && !replying && (
          <button
            onClick={() => (user ? setReplying(true) : onLogin())}
            data-testid={`discussion-reply-btn-${c.comment_id}`}
            className="mt-2 inline-flex items-center gap-1 text-xs font-semibold text-[#1E3A8A] hover:text-[#E07A5F] transition-colors"
          >
            <CornerDownRight size={12} /> Balas
          </button>
        )}
        {replying && (
          <Composer compact slug={slug} parentId={c.comment_id} onCancel={() => setReplying(false)}
            onPosted={(r) => { onReply(c.comment_id, r); setReplying(false); }} />
        )}
        {c.replies.length > 0 && (
          <ul className="mt-4 space-y-4 border-l-2 border-[#F0E6DF] pl-4" data-testid={`discussion-replies-${c.comment_id}`}>
            {c.replies.map((r) => (
              <li key={r.comment_id}><CommentBody c={r} onReport={onReport} user={user} /></li>
            ))}
          </ul>
        )}
      </div>
    </li>
  );
}

export function ArticleDiscussion({ slug }) {
  const { article_discussions_enabled, loaded } = useFlags();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [reporting, setReporting] = useState(null);

  const load = useCallback(() => {
    api.get(`/blog/${slug}/discussion`).then((r) => setData(r.data)).catch(() => setData({ enabled: false }));
  }, [slug]);
  useEffect(() => { if (loaded && article_discussions_enabled) load(); }, [loaded, article_discussions_enabled, load]);

  if (!data || !data.enabled) return null;
  const open = data.status !== "closed";
  const onLogin = () => promptLogin(navigate);
  const addTop = (c) => setData((d) => ({ ...d, count: d.count + 1, comments: [...d.comments, c] }));
  const addReply = (pid, r) => setData((d) => ({
    ...d, count: d.count + 1,
    comments: d.comments.map((c) => (c.comment_id === pid ? { ...c, replies: [...c.replies, r] } : c)),
  }));

  return (
    <section className="mt-14 pt-10 border-t border-[#E8E4DB]" data-testid="article-discussion">
      <div className="flex items-center gap-2">
        <MessageSquareText size={18} className="text-[#D2673E]" />
        <h2 className="font-serif-display text-2xl text-[#0F1F3D]">Diskusi Artikel</h2>
        <span className="ml-1 text-sm text-[#8A8F99] tabular-nums" data-testid="article-discussion-count">({data.count})</span>
      </div>
      <p className="mt-1 text-sm text-[#5C6472]">Tanggapan langsung tampil setelah dikirim. Mohon jaga diskusi tetap sopan dan relevan.</p>

      <div className="mt-6">
        {!open ? (
          <div className="flex items-center gap-2 p-4 rounded-xl bg-[#F4F2EE] text-sm text-[#4B5563]" data-testid="article-discussion-closed">
            <Lock size={14} /> Diskusi untuk artikel ini telah ditutup. Tanggapan sebelumnya tetap dapat dibaca.
          </div>
        ) : user ? (
          <Composer slug={slug} onPosted={addTop} />
        ) : (
          <div className="p-5 rounded-xl border border-dashed border-[#E5E1D8] bg-white flex flex-col sm:flex-row sm:items-center justify-between gap-3" data-testid="article-discussion-login-prompt">
            <span className="text-sm text-[#4B5563]">Masuk untuk ikut berdiskusi tentang artikel ini.</span>
            <Button onClick={onLogin} className="rounded-full bg-[#0F1F3D] hover:bg-[#1E3A8A] text-white" data-testid="article-discussion-login-btn">Masuk</Button>
          </div>
        )}
      </div>

      {data.comments.length === 0 ? (
        <p className="mt-8 text-sm text-[#8A8F99]" data-testid="article-discussion-empty">Belum ada tanggapan. Jadilah yang pertama.</p>
      ) : (
        <ul className="mt-4" data-testid="article-discussion-list">
          {data.comments.map((c) => (
            <Thread key={c.comment_id} c={c} slug={slug} open={open} user={user}
              onReport={setReporting} onReply={addReply} onLogin={onLogin} />
          ))}
        </ul>
      )}
      <ReportDialog comment={reporting} onClose={() => setReporting(null)} />
    </section>
  );
}
